import React, { useState } from "react";
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import {
    Controller,
    FormProvider,
    SubmitErrorHandler,
    SubmitHandler,
    useForm,
} from "react-hook-form";
import { Outlet, useLocation } from "react-router";
import Select from "react-select";

import FileUpload from "../../components/FileUpload";
import { alertActions } from "../../features/alert/alertSlice";
import { selectSelectedTeam } from "../../features/clubhouse/clubhouseSlice";
import {
    selectAgeLevels,
    selectSetting,
    selectUmpires,
} from "../../features/global/globalSlice";
import { selectUsers } from "../../features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { updateTeam } from "../../lib/api";
import {
    AUTH_LEVELS,
    TEAM_LOGO_URL,
    VALID_FILE_EXTENSIONS,
} from "../../lib/globals";
import { displayFormAlert, scrollToTop } from "../../lib/utils";
import { ITeam } from "../../types/api";
import { IClubhouseTeamInfoData } from "../../types/forms";

const ClubhouseTeam = (): React.JSX.Element => {
    const location = useLocation();
    const isOnTeamPage = /team\/\d+\/?$/.test(location.pathname);
    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.auth.user);

    const selectedTeam = useAppSelector(selectSelectedTeam);

    const umpires = useAppSelector(selectUmpires);
    const users = useAppSelector(selectUsers);
    const currentYear = useAppSelector(state =>
        selectSetting(state, "wibb_current_league_year"),
    );
    const currentSeason = useAppSelector(state =>
        selectSetting(state, "wibb_current_league_season"),
    );

    const allowedExtensions = VALID_FILE_EXTENSIONS.image.slice();
    const [fileList, setFileList] = useState<File[]>([]);
    const [team, setTeam] = useState<ITeam | undefined>(selectedTeam);
    const [phone, setPhone] = useState<string | null>(team!.coach.phone);

    const methods = useForm<IClubhouseTeamInfoData>({
        mode: "onChange",
    });

    const onSubmit: SubmitHandler<IClubhouseTeamInfoData> = async data => {
        data.files = fileList;

        await updateTeam(team!.id, data)
            .then(response => {
                dispatch(
                    alertActions.success({
                        title: `Team updated successfully`,
                        dismissible: true,
                    }),
                );

                setTeam(response.team);
                setFileList([]);
                methods.resetField("files");
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem updating the team.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const defaultCoach = users.find(u => u.id === team?.coach.id)
        ? {
              value: team!.coach.id,
              label: `${team?.coach.firstName} ${team?.coach.lastName} (${team?.coach.email})`,
          }
        : undefined;

    const leagueAgeLevel = team?.ageLevels.find(
        ta =>
            ta.year === parseInt(currentYear!.setting) &&
            ta.season.season === currentSeason!.setting,
    );

    const mostRecentTeamAgeLevel = team?.ageLevels.reduce((prev, current) => {
        return new Date(current.year, current.season.id) >
            new Date(prev.year, prev.season.id)
            ? current
            : prev;
    }, team.ageLevels[0]);

    const teamAgeLevel = leagueAgeLevel || mostRecentTeamAgeLevel;

    React.useEffect(() => {
        const subscription = methods.watch((value, { name, type }) => {
            if (name === "coach" && type == "change") {
                setPhone(
                    users.find(u => u.id === value.coach?.value)!.meta
                        .phoneNumbers.primary,
                );
            }
        });
        return () => subscription.unsubscribe();
    }, [methods.watch]);

    const onError: SubmitErrorHandler<IClubhouseTeamInfoData> = data => {
        console.log(data);
    };

    return isOnTeamPage ? (
        <section className="teamInfo">
            <FormProvider {...methods}>
                <Form
                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                    id="clubhouseTeamInfoForm"
                >
                    <Row>
                        <Col>
                            <Form.Group controlId="name">
                                <Form.Label>Team Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    {...methods.register("name", {
                                        required: "Team Name is required",
                                        value: team?.name,
                                    })}
                                    aria-invalid={
                                        methods.formState.errors.name
                                            ? "true"
                                            : "false"
                                    }
                                />
                                <Form.Text>
                                    Note: Please do not include age level or
                                    league designation in your team's name
                                </Form.Text>
                                {methods.formState.errors.name &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.name
                                            .message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="coach">
                                <Form.Label>Team Contact</Form.Label>
                                <Controller
                                    name="coach"
                                    control={methods.control}
                                    defaultValue={defaultCoach}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={true}
                                            defaultValue={defaultCoach}
                                            options={users.map(u => {
                                                return {
                                                    value: parseInt(
                                                        u.id.toString(),
                                                    ),
                                                    label: `${u.meta.firstName} ${u.meta.lastName} (${u.email})`,
                                                };
                                            })}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>Team Logo</Form.Label>
                            {team?.logo && team.logo !== "" && (
                                <img
                                    className="teamLogo"
                                    src={`${TEAM_LOGO_URL}${team.logo}`}
                                />
                            )}
                            <Form.Group controlId="files">
                                <FileUpload
                                    maxFileCount={1}
                                    allowedFileExtensions={allowedExtensions}
                                    maxFileSize={1024000}
                                    fileList={fileList}
                                    setFileList={setFileList}
                                />
                                <Form.Label className="small">
                                    Accepted file types: jpg, jpeg, png
                                    <br />
                                    Max file size: 1 MB
                                </Form.Label>
                                <p className="small">
                                    Upload a new logo to replace current one
                                </p>
                            </Form.Group>
                            {methods.formState.errors.files &&
                                displayFormAlert({
                                    level: "danger",
                                    text: methods.formState.errors.files
                                        .message as string,
                                })}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>Contact Number</Form.Label>
                            <p>{phone}</p>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>Team Age Level</Form.Label>
                            <p>{teamAgeLevel?.ageLevel}</p>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button variant="primary" type="submit">
                                Update Information
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </FormProvider>
        </section>
    ) : (
        <Outlet />
    );
};

export default ClubhouseTeam;
